import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import type { MudancaAprovacao } from '@/types/mudanca';

interface AprovacaoWithUser extends MudancaAprovacao {
  aprovador?: {
    id: string;
    name: string;
    email: string;
    avatar_url?: string;
  };
}

export const useMudancaAprovacoes = (mudancaId?: string) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Buscar aprovações de uma mudança com dados do aprovador
  const { data: aprovacoes = [], isLoading } = useQuery({
    queryKey: ['mudanca-aprovacoes', mudancaId],
    queryFn: async () => {
      if (!mudancaId) return [];
      
      const { data, error } = await supabase
        .from('mudanca_aprovacoes' as any)
        .select(`
          *,
          aprovador:users!mudanca_aprovacoes_aprovador_id_fkey (
            id,
            name,
            email,
            avatar_url
          )
        `)
        .eq('mudanca_id', mudancaId)
        .order('nivel_aprovacao', { ascending: true });

      if (error) throw error;
      return ((data || []) as any) as AprovacaoWithUser[];
    },
    enabled: !!mudancaId,
  });

  // Criar aprovação
  const createAprovacao = useMutation({
    mutationFn: async (aprovacao: Partial<MudancaAprovacao>) => {
      const { data, error } = await supabase
        .from('mudanca_aprovacoes' as any)
        .insert({
          ...aprovacao,
          status_aprovacao: 'pendente',
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mudanca-aprovacoes'] });
    },
  });

  // Aprovar mudança
  const aprovarMudanca = useMutation({
    mutationFn: async ({ 
      aprovacaoId, 
      justificativa,
      mudancaId 
    }: { 
      aprovacaoId: string; 
      justificativa?: string;
      mudancaId: string;
    }) => {
      const { data: user } = await supabase.auth.getUser();
      
      // Atualizar aprovação
      const { error: aprovacaoError } = await supabase
        .from('mudanca_aprovacoes' as any)
        .update({
          status_aprovacao: 'aprovada',
          justificativa,
          data_aprovacao: new Date().toISOString(),
        })
        .eq('id', aprovacaoId);

      if (aprovacaoError) throw aprovacaoError;

      // Verificar se todas aprovações foram concluídas
      const { data: todasAprovacoes, error: fetchError } = await supabase
        .from('mudanca_aprovacoes' as any)
        .select('status_aprovacao')
        .eq('mudanca_id', mudancaId);

      if (fetchError) throw fetchError;

      const todasAprovadas = todasAprovacoes?.every(
        (a: any) => a.status_aprovacao === 'aprovada'
      );

      // Se todas aprovadas, mudar status da mudança para "aprovada"
      if (todasAprovadas) {
        const { error: mudancaError } = await supabase
          .from('mudancas' as any)
          .update({
            status: 'aprovada',
            data_aprovacao: new Date().toISOString(),
            atualizado_por: user.user?.id,
            atualizado_em: new Date().toISOString(),
          })
          .eq('id', mudancaId);

        if (mudancaError) throw mudancaError;

        // Criar etapas de workflow automaticamente
        await supabase.from('mudanca_workflow' as any).insert([
          { mudanca_id: mudancaId, etapa: 'criacao', status: 'concluida', ordem: 1 },
          { mudanca_id: mudancaId, etapa: 'avaliacao', status: 'concluida', ordem: 2 },
          { mudanca_id: mudancaId, etapa: 'aprovacao', status: 'concluida', ordem: 3 },
          { mudanca_id: mudancaId, etapa: 'execucao', status: 'pendente', ordem: 4 },
          { mudanca_id: mudancaId, etapa: 'validacao', status: 'pendente', ordem: 5 },
          { mudanca_id: mudancaId, etapa: 'encerramento', status: 'pendente', ordem: 6 },
        ]);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mudanca-aprovacoes'] });
      queryClient.invalidateQueries({ queryKey: ['mudancas'] });
      queryClient.invalidateQueries({ queryKey: ['mudanca-workflow'] });
      toast({
        title: "Sucesso",
        description: "Mudança aprovada com sucesso!",
      });
    },
    onError: (error) => {
      console.error('Erro ao aprovar mudança:', error);
      toast({
        title: "Erro",
        description: "Erro ao aprovar mudança. Tente novamente.",
        variant: "destructive",
      });
    },
  });

  // Rejeitar mudança
  const rejeitarMudanca = useMutation({
    mutationFn: async ({ 
      aprovacaoId, 
      justificativa,
      mudancaId 
    }: { 
      aprovacaoId: string; 
      justificativa: string;
      mudancaId: string;
    }) => {
      const { data: user } = await supabase.auth.getUser();
      
      // Atualizar aprovação
      const { error: aprovacaoError } = await supabase
        .from('mudanca_aprovacoes' as any)
        .update({
          status_aprovacao: 'rejeitada',
          justificativa,
          data_aprovacao: new Date().toISOString(),
        })
        .eq('id', aprovacaoId);

      if (aprovacaoError) throw aprovacaoError;

      // Mudar status da mudança para "cancelada"
      const { error: mudancaError } = await supabase
        .from('mudancas' as any)
        .update({
          status: 'cancelada',
          atualizado_por: user.user?.id,
          atualizado_em: new Date().toISOString(),
        })
        .eq('id', mudancaId);

      if (mudancaError) throw mudancaError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mudanca-aprovacoes'] });
      queryClient.invalidateQueries({ queryKey: ['mudancas'] });
      toast({
        title: "Mudança Rejeitada",
        description: "A mudança foi cancelada.",
      });
    },
    onError: (error) => {
      console.error('Erro ao rejeitar mudança:', error);
      toast({
        title: "Erro",
        description: "Erro ao rejeitar mudança. Tente novamente.",
        variant: "destructive",
      });
    },
  });

  return {
    aprovacoes,
    isLoading,
    createAprovacao,
    aprovarMudanca,
    rejeitarMudanca,
  };
};
